/**
 * Centralized Axios API Client
 * Handles: auth tokens, request/response interceptors, error normalization
 */

import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ─── Request Interceptor ─────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ────────────────────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response.data, // unwrap .data by default
  (error) => {
    const status = error.response?.status;

    // Auto logout on 401/403
    if (status === 401 || status === 403) {
      localStorage.clear();
      window.location.reload();
      return Promise.reject(error);
    }

    // Normalize error message
    const message =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      getDefaultErrorMessage(status);

    return Promise.reject({ ...error, message });
  }
);

const getDefaultErrorMessage = (status) => {
  const messages = {
    400: "Invalid request. Please check your details.",
    422: "Please check all required fields and try again.",
    404: "Resource not found.",
    429: "Too many requests. Please slow down.",
    500: "Server error. Please try again later.",
    502: "Service temporarily unavailable.",
    503: "Service under maintenance. Try again shortly.",
  };
  return messages[status] || "Your request could not be completed. Please try again.";
};

export default apiClient;